import React from 'react'
import { Button, Flex, Text, TextVariant } from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'hooks/useRouter'

const StyledText = styled(Text)`
  white-space: pre-wrap;
  text-align: center;
`

/**
 * Why the table is empty, which is not one question but two.
 *
 * A community member wrote in today saying "I can't see my licence. What must
 * I do?" — and they hold one. They had not managed to complete the sign-in
 * signature, so the app had no session, so the list was empty, so this screen
 * told them they had no licences and offered to sell them one.
 *
 * **Telling a holder they own nothing is not an empty state, it is a false
 * statement about their property.** An app that cannot see somebody's licences
 * must say it cannot see them.
 */
export type EmptyReason = 'not-signed-in' | 'none'

const EmptyNodes: React.FC<{ reason?: EmptyReason; onSignIn?: () => void }> = ({
  reason = 'none',
  onSignIn
}) => {
  const { t } = useTranslation()
  const { push } = useRouter()
  const { colors } = useTheme()

  if (reason === 'not-signed-in') {
    return (
      <Flex flexDirection="column" alignItems="center" flexGrow={1}>
        <Flex flexDirection="column" justifyContent="center" alignItems="center" flexGrow={1} maxWidth="640px">
          <StyledText variant={TextVariant.BODY_BIG_BOLD}>{t('licenses.signInTitle')}</StyledText>
          <Text variant={TextVariant.BODY_DEFAULT} color={colors.gray6} textAlign="center">
            {t('licenses.signInDescription')}
          </Text>
          {onSignIn ? (
            <Flex mt="16px">
              <Button onClick={onSignIn}>{t('licenses.signInAction')}</Button>
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex flexDirection="column" alignItems="center" flexGrow={1}>
      <Flex flexDirection="column" justifyContent="center" alignItems="center" flexGrow={1} maxWidth="640px">
        <StyledText variant={TextVariant.BODY_BIG_BOLD}>{t('licenses.emptyTableTitle')}</StyledText>
        <Text variant={TextVariant.BODY_DEFAULT} color={colors.gray6} textAlign="center">
          {t('licenses.emptyTableDescription')}
        </Text>
        <Flex mt="16px">
          {/*
            The marketplace, not this page. The button used to push to
            `/user-licenses` — the screen it is rendered on — so the one action
            offered to somebody with no licences did nothing at all.
          */}
          <Button onClick={() => push('/marketplace')}>{t('licenses.getLicense')}</Button>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default EmptyNodes
